import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { usePosWarehousesQuery } from "@/modules/cf-feed/presentation/api/queries";
import type {
  WorkspaceCommand,
  WorkspacePreview,
  WorkspaceSnapshot,
} from "../../domain/contracts/live-workspace";
import type { ProductSet } from "../api/product-set-client";
import {
  workspaceErrorMessage,
  workspaceRequest,
} from "../api/workspace-client";
import { WorkspaceProductCard } from "./WorkspaceProductCard";
import { kip } from "./WorkspaceProductEditor";

export function ProductSetReviewItems({
  items,
}: {
  items: WorkspacePreview["items"];
}) {
  const groups = new Map<string, WorkspacePreview["items"]>();
  for (const item of items) {
    const key = item.productId ?? item.id;
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  }
  if (items.length === 0)
    return <p className="text-muted-foreground text-sm">ບໍ່ມີ SKU ໃນຊຸດນີ້</p>;
  return (
    <div className="space-y-3">
      <output className="block text-sm">
        {groups.size} ສິນຄ້າ · {items.length} SKU · ເພີ່ມໄດ້{" "}
        {
          items.filter(
            (item) => item.status === "ready" || item.status === "prepared",
          ).length
        }{" "}
        · ຂ້າມ {items.filter((item) => item.status === "skipped").length} · ໃຊ້ບໍ່ໄດ້{" "}
        {items.filter((item) => item.status === "failed").length}
      </output>
      {[...groups].map(([productId, variants]) => (
        <WorkspaceProductCard
          key={productId}
          productId={productId}
          name={variants[0]?.productName ?? "ບໍ່ພົບຂໍ້ມູນສິນຄ້າ"}
          subtitle={`${variants.length} SKU`}
          defaultOpen
          variantLayout="content"
          variants={variants.map((item) => ({
            id: item.id,
            selectionLabel: item.cfCode,
            content: (
              <div className="space-y-1 py-1 text-xs">
                <p className="font-semibold text-sm">{item.cfCode}</p>
                <p className="text-muted-foreground">
                  {item.status === "skipped"
                    ? "ມີຢູ່ແລ້ວ · ຂ້າມ ແລະຮັກສາລາຄາເດີມ"
                    : item.status === "prepared"
                      ? "ເພີ່ມແຕ່ປິດຂາຍ · ກວດສາງ / ສະຕັອກ"
                      : item.status === "ready"
                        ? "ພ້ອມຂາຍ"
                        : "ໃຊ້ບໍ່ໄດ້"}
                </p>
                {item.status === "failed" ? (
                  <p>{workspaceErrorMessage(new Error(item.error))}</p>
                ) : (
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-1 tabular-nums sm:grid-cols-2">
                    {item.status !== "skipped" ? (
                      <>
                        <div className="flex flex-wrap justify-between gap-x-2">
                          <dt>ລາຄາປົກກະຕິ</dt>
                          <dd>{kip(item.before)}</dd>
                        </div>
                        <div className="flex flex-wrap justify-between gap-x-2">
                          <dt>
                            {item.after > item.before ? "ປັບລາຄາເພີ່ມ" : "ສ່ວນຫຼຸດ"}
                          </dt>
                          <dd>{kip(Math.abs(item.before - item.after))}</dd>
                        </div>
                      </>
                    ) : null}
                    <div className="flex flex-wrap justify-between gap-x-2">
                      <dt>
                        {item.status === "skipped" ? "ລາຄາຂາຍໃນງານ" : "ຫຼັງຫຼຸດ"}
                      </dt>
                      <dd>{kip(item.after)}</dd>
                    </div>
                    <div className="flex flex-wrap justify-between gap-x-2">
                      <dt>ບໍລິການເພີ່ມ</dt>
                      <dd>{kip(item.options)}</dd>
                    </div>
                    <div className="flex flex-wrap justify-between gap-x-2 font-semibold sm:col-span-2">
                      <dt>ລວມ / ຊິ້ນ</dt>
                      <dd>{kip(item.after + item.options)}</dd>
                    </div>
                  </dl>
                )}
              </div>
            ),
          }))}
        />
      ))}
    </div>
  );
}

export type ProductSetApplyResult = {
  revision: number;
  failed?: Array<{ id: string; code: string }>;
  added?: string[];
  prepared?: string[];
  skipped?: Array<{ variantId: string; code: "PRODUCT_ALREADY_IN_WORKSPACE" }>;
};
export type ProductSetApply = (
  templateId: string,
  preview: WorkspacePreview,
) => Promise<ProductSetApplyResult | false>;

export function WorkspaceProductSetReview({
  template,
  workspace,
  onApply,
  onClose,
  onRefresh,
}: {
  template: ProductSet;
  workspace: WorkspaceSnapshot;
  onApply: ProductSetApply;
  onClose: () => void;
  onRefresh: () => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [result, setResult] = useState<ProductSetApplyResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const warehouses = usePosWarehousesQuery();
  const preview = useQuery({
    queryKey: [
      "product-set-apply-preview",
      workspace.id,
      workspace.revision,
      template.id,
      template.revision,
    ],
    queryFn: () =>
      workspaceRequest<WorkspacePreview>(`/${workspace.id}/preview`, {
        key: crypto.randomUUID(),
        revision: workspace.revision,
        action: { type: "apply-product-set", templateId: template.id },
      } satisfies WorkspaceCommand),
    staleTime: 0,
  });
  const inspected = preview.data;
  const fresh =
    inspected?.revision === workspace.revision &&
    inspected.templateRevision === template.revision &&
    Boolean(inspected.fingerprint);
  const apply = async () => {
    if (
      !fresh ||
      !inspected ||
      preview.isError ||
      preview.isFetching ||
      lock.current
    )
      return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      const response = await onApply(template.id, inspected);
      if (!response) {
        setError(new Error("REQUEST_FAILED"));
        void preview.refetch();
      } else setResult(response);
    } catch (caught) {
      setError(caught);
      void preview.refetch();
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-3xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>ກວດຊຸດ {template.name}</DialogTitle>
          <DialogDescription>
            ງານ: {workspace.title} · ເພີ່ມສະເພາະ SKU ທີ່ຍັງບໍ່ມີ
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          <p className="text-sm">
            ສາງ:{" "}
            {workspace.warehouseId
              ? (warehouses.data?.items.find(
                  (item) => item.id === workspace.warehouseId,
                )?.name ??
                (warehouses.isError
                  ? "ກວດຊື່ສາງບໍ່ສຳເລັດ"
                  : warehouses.isPending
                    ? "ກຳລັງກວດຊື່ສາງ…"
                    : "ບໍ່ພົບຊື່ສາງ"))
              : "ຍັງບໍ່ເລືອກສາງ · ເພີ່ມແຕ່ປິດຂາຍ"}
          </p>
          {inspected?.promo ? (
            <div className="space-y-1 text-sm">
              <p>
                ໂປຣຂອງງານ:{" "}
                {inspected.promo.listDiscountFixedLak != null
                  ? kip(inspected.promo.listDiscountFixedLak)
                  : `${inspected.promo.listDiscountPercent ?? 0}%`}
              </p>
              <p>
                ຄ່າສົ່ງ:{" "}
                {inspected.promo.freeShipping
                  ? "ຟຣີ"
                  : inspected.promo.shippingFeeLak == null
                    ? "ຍັງບໍ່ກຳນົດ"
                    : kip(inspected.promo.shippingFeeLak)}
              </p>
              {inspected.promo.options.map((option) => (
                <p key={option.adminOptionDefinitionId}>
                  {option.label} · {kip(option.surchargeLak)} ·{" "}
                  {option.isAutoAttach ? "ແນບອັດຕະໂນມັດ" : "ເລືອກເພີ່ມ"}
                </p>
              ))}
            </div>
          ) : null}
          {preview.isPending ? (
            <output className="block">ກຳລັງກວດລາຄາ ແລະສະຕັອກ…</output>
          ) : preview.isError ? (
            <p role="alert">{workspaceErrorMessage(preview.error)}</p>
          ) : inspected ? (
            <ProductSetReviewItems items={inspected.items} />
          ) : null}
          <p className="text-muted-foreground text-xs">
            ລາຄາຕໍ່ຊິ້ນບໍ່ລວມຄ່າສົ່ງ · ແກ້ຊຸດພາຍຫຼັງບໍ່ປ່ຽນງານທີ່ເຄີຍເພີ່ມແລ້ວ
          </p>
          {!preview.isPending && !preview.isError && !fresh ? (
            <p role="alert">
              {workspaceErrorMessage(new Error("PRODUCT_SET_PREVIEW_STALE"))}
            </p>
          ) : null}
          {error ? <p role="alert">{workspaceErrorMessage(error)}</p> : null}
          {result ? (
            <output className="block">
              <p>
                ເພີ່ມພ້ອມຂາຍ {result.added?.length ?? 0} · ເພີ່ມແຕ່ປິດຂາຍ{" "}
                {result.prepared?.length ?? 0} · ມີຢູ່ແລ້ວ{" "}
                {result.skipped?.length ?? 0} · ໃຊ້ບໍ່ໄດ້{" "}
                {result.failed?.length ?? 0}
              </p>
              {result.failed?.map((failure) => (
                <p key={failure.id}>
                  {inspected?.items.find((item) => item.id === failure.id)
                    ?.cfCode ?? "SKU"}
                  : {workspaceErrorMessage(new Error(failure.code))}
                </p>
              ))}
            </output>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t pt-3">
          <Button variant="outline" disabled={busy} onClick={onClose}>
            {result ? "ປິດ" : "ຍົກເລີກ"}
          </Button>
          {!result ? (
            <>
              <Button
                variant="secondary"
                disabled={busy || preview.isFetching}
                onClick={() => {
                  setError(null);
                  void onRefresh()
                    .then(() => preview.refetch())
                    .catch(setError);
                }}
              >
                ກວດໃໝ່
              </Button>
              <Button
                disabled={
                  busy ||
                  preview.isFetching ||
                  preview.isError ||
                  !fresh ||
                  !inspected?.items.some(
                    (item) =>
                      item.status === "ready" || item.status === "prepared",
                  )
                }
                onClick={() => void apply()}
              >
                {busy ? "ກຳລັງເພີ່ມ…" : "ຢືນຢັນເພີ່ມຊຸດ"}
              </Button>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
